/** The social providers Settings offers, and how each one is described (Figma 09 · Social accounts). */
import type { ComponentType } from "react";
import { Facebook, Instagram, Youtube } from "lucide-react";
import type { SocialConnection, SocialProvider } from "@/app/routes/dashboard/settings/api/useSocialConnections";
import { TikTokIcon } from "../../socialGlyphs";

export type ProviderInfo = {
  provider: SocialProvider;
  label: string;
  /** The row name when there is no account ("Facebook Page"). */
  accountNoun: string;
  icon: ComponentType<{ className?: string }>;
  canPublish: string;
  needs: (brand: string) => string;
  signIn: string;
  after: (brand: string) => string;
};

/** Snapchat is parked (its routes are unmounted), so it is not offered. */
export const PROVIDERS: ProviderInfo[] = [
  {
    provider: "INSTAGRAM",
    label: "Instagram",
    accountNoun: "Instagram professional account",
    icon: Instagram,
    canPublish: "feed posts, reels and stories",
    needs: () => "An Instagram professional account linked to a Facebook Page",
    signIn: "The Facebook login that manages that Page",
    after: (brand) => `The Instagram accounts linked to that login’s Pages are added to ${brand}.`,
  },
  {
    provider: "FACEBOOK",
    label: "Facebook",
    accountNoun: "Facebook Page",
    icon: Facebook,
    canPublish: "Page posts and reels",
    needs: (brand) => `A Facebook Page for ${brand}`,
    signIn: "A Facebook login that manages the Page",
    after: (brand) => `The Pages that login manages are added to ${brand}.`,
  },
  {
    provider: "TIKTOK",
    label: "TikTok",
    accountNoun: "TikTok account",
    icon: TikTokIcon,
    canPublish: "videos",
    needs: (brand) => `${brand}’s TikTok account`,
    signIn: "That TikTok account",
    after: (brand) => `The account is added to ${brand}.`,
  },
  {
    provider: "YOUTUBE",
    label: "YouTube",
    accountNoun: "YouTube channel",
    icon: Youtube,
    canPublish: "videos and shorts",
    needs: () => "A YouTube channel",
    signIn: "The Google account that owns the channel",
    after: (brand) => `The channels that Google account owns are added to ${brand}.`,
  },
];

export const providerInfo = (provider: string): ProviderInfo =>
  PROVIDERS.find((p) => p.provider === provider) ?? PROVIDERS[0];

/** `?connected=` value → provider. */
export function providerFromParam(value: string | null | undefined): ProviderInfo | null {
  if (!value) return null;
  return PROVIDERS.find((p) => p.provider.toLowerCase() === value.toLowerCase()) ?? null;
}

export function socialErrorMessage(code: string, provider?: ProviderInfo | null): string {
  const name = provider?.label ?? "the platform";
  switch (code) {
    case "access_denied":
    case "user_denied":
    case "user_cancelled_login":
    case "user_cancelled_authorize":
      return `You cancelled on ${name}, so nothing was connected.`;
    case "missing_code":
      return "The sign-in did not finish, so nothing was connected. Try again.";
    case "no_instagram":
      return "This login has no Instagram professional account linked to a Facebook Page. Sign in with the login that manages it, or switch the Instagram account to Business or Creator and link it to a Page.";
    case "no_youtube_channel":
      return "That Google account has no YouTube channel. Sign in with the Google account that owns the channel.";
    default:
      return `${name === "the platform" ? "The account" : name} could not connect. Nothing changed here; try again.`;
  }
}

export type ConnectionHealth = "connected" | "attention" | "checking";

export function connectionHealth(
  connection: SocialConnection,
  canPublish: boolean | undefined,
  synced: boolean,
): ConnectionHealth {
  if (connection.status === "NEEDS_REAUTH" || canPublish === false) return "attention";
  if (!synced) return "checking";
  return "connected";
}
